// src/components/CabRentalBookingModal.js
import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Offcanvas,
  Button,
  Form,
  Row,
  Col,
  Card,
  Image,
  InputGroup,
} from "react-bootstrap";
import { Calendar, User, Phone } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import initiateRazorpayPayment from "./Razorpay";
import { v4 as uuidv4 } from "uuid";
import { db } from "../FireBase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  limit,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { AuthContext } from "../context/AuthContext";
import PaymentLoading from "./PaymentLoading";
import SuccessModal from "./SuccessModal";
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const MOBILE_BREAKPOINT = 768;
const ACCENT = "#54b435";

export default function CabRentalBookingModal({
  show,
  onHide,
  vehicles = [],
  vehiclePrices = {},
  packageData,
}) {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const { currentUser } = useContext(AuthContext);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (vehicles.length > 0) {
      const timer = setTimeout(() => setLoadingVehicles(false), 800);
      return () => clearTimeout(timer);
    }
  }, [vehicles]);

  useEffect(() => {
    if (!show) {
      setStep(1);
      setSelectedDate(null);
      setSelectedVehicle(null);
      setName("");
      setPhone("");
      setPickup("");
    }
  }, [show]);

  const getPriceFor = (id) => {
    const v = vehiclePrices?.[id];
    if (v === undefined || v === null) return null;
    const num = parseFloat(String(v).replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(num) ? num : null;
  };

  const selectedPrice = getPriceFor(selectedVehicle);
  const selectedCar = vehicles.find((v) => v.id === selectedVehicle) || null;

  const canProceedStep1 = selectedDate && name.trim() && phone.trim();
  const canProceedStep2 = selectedVehicle !== null;

  const goNext = () => {
    if (step === 1 && !canProceedStep1) return;
    if (step === 2 && !canProceedStep2) return;
    setStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

      const formatDateForOrder = (date) => {
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const initiatePayment = async () => {
    const selectedCarObj = vehicles.find((v) => v.id === selectedVehicle) || null;
    const orderId = "CAB" + uuidv4().slice(0, 8).toUpperCase();
    const transactionId = "TR" + Date.now().toString().slice(-6);

    const bookingData = {
      date: formatDateForOrder(selectedDate),
      from: packageData.from,
      to: packageData.to,
      vehicleDetails: selectedCarObj,
      name: name.trim(),
      phone: phone.trim(),
      pickup: pickup.trim(),
      drop:drop.trim(),
      userId: currentUser.cusmid,
      userName: currentUser.name,
      userEmail: currentUser.email,
      userPhone: currentUser.phoneNumber,
      price: selectedPrice,
      paymentStatus: "Pending",
      status: "payment-pending",
      paymentMethod: 'Razorpay',
      orderId,
      transactionId,
      createdAt: serverTimestamp(),
    };

    const transactionData = {
      userId: currentUser.cusmid,
      orderId,
      transactionId,
      paymentAmount: selectedPrice,
      paymentDate: serverTimestamp(),
      type: "OUTSTATION_RENTAL",
    };

    try {
       onHide(); // close booking modal first
      setLoadingPayment(true);
     
      await addDoc(collection(db, "outstation_rental_booking"), bookingData);
      await addDoc(collection(db, "transactions"), transactionData);

      const success = await initiateRazorpayPayment(
        selectedPrice,
        transactionId,
        currentUser.phoneNumber,
        orderId
      );
      if (!success) throw new Error("Payment failed");

      const razorpayId = success.razorpay_payment_id;

      const q = query(collection(db, "transactions"), where("orderId", "==", orderId), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        await updateDoc(snap.docs[0].ref, {
          razorpayId,
          status: "paid",
        });
        setOrderPlaced(true);
      }
      setLoadingPayment(false);
      setTimeout(() => {
          setLoadingPayment(false);
          setOrderPlaced(true);  
      }, 800);
    } catch (err) {
      console.error("Payment failed:", err);
      alert("Payment failed. Please try again.");
      setLoadingPayment(false);
    }
  };

  const StepIndicator = () => {
    const steps = ["Date & Guest", "Choose Vehicle", "Pickup & Pay"];
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        {steps.map((label, i) => {
          const stepNum = i + 1;
          const active = step === stepNum;
          const done = step > stepNum;
          return (
            <div key={i} className="text-center flex-fill">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  lineHeight: "36px",
                  background: active || done ? ACCENT : "#e9ecef",
                  color: active || done ? "white" : "#6c757d",
                  margin: "0 auto",
                  fontWeight: 700,
                }}
              >
                {stepNum}
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>{label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const Step1 = (
    <div className="text-center">
      <Form.Group className="mb-4 w-100 text-center">
      <Form.Label className="fw-semibold d-block mb-2">
                <Calendar className="me-2" size={18} /> Select Date
              </Form.Label>
        <div className="rounded shadow-sm p-2 bg-light d-inline-block">
          <DayPicker mode="single" selected={selectedDate} onSelect={setSelectedDate} />
        </div>
      </Form.Group>

      <Form.Group className="mb-3 w-75 mx-auto">
        <Form.Label>Name</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <User size={16} />
          </InputGroup.Text>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter guest name"
          />
        </InputGroup>
      </Form.Group>

      <Form.Group className="mb-3 w-75 mx-auto">
        <Form.Label>Phone</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <Phone size={16} />
          </InputGroup.Text>
          <Form.Control
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, ""))}
            placeholder="Enter phone number"
          />
        </InputGroup>
      </Form.Group>

      <Button variant="success" onClick={goNext} disabled={!canProceedStep1} style={{ background: ACCENT, borderColor: ACCENT }}>
        Next ➜
      </Button>
    </div>
  );

  const Step2 = (
    <div>
      <div className="text-center mb-3">
        <div className="fw-semibold">Choose a Vehicle</div>
        <div className="text-muted" style={{ fontSize: 13 }}>
          Tap to select vehicle for your trip
        </div>
      </div>
      <div style={{ maxHeight: 390, overflowY: "auto" }}>
        {loadingVehicles
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-light rounded p-4 mb-3" />
            ))
          : vehicles.map((v) => {
              const selected = selectedVehicle === v.id;
              const price = getPriceFor(v.id);
              return (
                <Card
                  key={v.id}
                  className={`shadow-sm mb-3 p-2 ${selected ? "border-success" : ""}`}
                  onClick={() => setSelectedVehicle(v.id)}
                  style={{
                    cursor: "pointer",
                    borderColor: selected ? ACCENT : "transparent",
                    backgroundColor: selected ? "#f0fff4" : "#fff",
                  }}
                >
                  <Row className="align-items-center g-2">
                    <Col xs={3}>
                      <Image src={v.image} alt={v.name} rounded fluid />
                    </Col>
                    <Col xs={6}>
                      <div className="fw-semibold">{v.name}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Seats: {v.capacity}
                      </div>
                    </Col>
                    <Col xs={3} className="text-end fw-bold" style={{ color: ACCENT }}>
                      ₹{price?.toLocaleString("en-IN")}
                    </Col>
                  </Row>
                </Card>
              );
            })}
      </div>
      <div className="d-flex justify-content-between mt-3">
        <Button variant="light" onClick={goBack}>
          ← Back
        </Button>
        <Button variant="success" onClick={goNext} disabled={!selectedVehicle} style={{ background: ACCENT, borderColor: ACCENT }}>
          Next ➜
        </Button>
      </div>
    </div>
  );

  const Step3 = (
    <div>
     
      
      <Form.Group className="mb-3">
        <Form.Label>Pickup Location in({packageData.from})</Form.Label>
        <Form.Control
          value={pickup}
          onChange={(e) => setPickup(e.target.value)}
          placeholder="Enter pickup point"
          style={{width: '100%'}}
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Drop Location  in({packageData.to})</Form.Label>
        <Form.Control
          value={drop}
          onChange={(e) => setDrop(e.target.value)}
          placeholder="Enter pickup point"
          style={{width: '100%'}}
          required
        />
      </Form.Group>

      <div className="p-3 my-3 rounded shadow-sm" style={{ background: "#fafbf9" }}>
        <div className="d-flex justify-content-between">
          <div>
            <div className="text-muted">Selected Vehicle</div>
            <div className="fw-semibold">{selectedCar?.name}</div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              {packageData.from} → {packageData.to}
            </div>
          </div>
          <div className="text-end">
            <div className="text-muted">Price</div>
            <div className="fw-bold" style={{ color: ACCENT, fontSize: 18 }}>
              ₹{selectedPrice?.toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between">
        <Button variant="light" onClick={goBack}>
          ← Back
        </Button>
        <Button
          variant="success"
          onClick={initiatePayment}
          disabled={!selectedPrice || loadingPayment}
          style={{ background: ACCENT, borderColor: ACCENT }}
        >
          {loadingPayment ? "Processing..." : `Pay ₹${selectedPrice}`}
        </Button>
      </div>
    </div>
  );

  const content = (
    <Form onSubmit={(e) => e.preventDefault()}>
      <StepIndicator />
      {step === 1 && Step1}
      {step === 2 && Step2}
      {step === 3 && Step3}
    </Form>
  );


return (
  <>
    {isMobile ? (
       <Offcanvas
      show={show}
      onHide={onHide}
      placement="bottom"
      style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 ,height: 'auto'}}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Book Cab Rental</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>{content}</Offcanvas.Body>
    </Offcanvas>
    ) : (
      <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Book Cab Rental</Modal.Title>
      </Modal.Header>
      <Modal.Body>{content}</Modal.Body>
    </Modal>
    )}

    {/* Global Overlays */}
    {loadingPayment && <PaymentLoading show />}
    {orderPlaced && <SuccessModal show onClose={() => navigate('/')} />}
  </>
)}
